import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useStore } from "zustand";
import AppLoading from "components/AppLoading/AppLoading";
import assert from "assert";
import { isDefined } from "utils/isDefined";
import { ContextOutOfBoundsError } from "providers/ContextOutOfBoundsError";
import { DataTableStore, DataTableStoreState } from "./DataTableProvider.types";
import { createDataTableStore } from "./DataTableProvider.helpers";
import { Project } from "graphql/_Types";
import { useFileUploadContext } from "stores/upload/FileUploadProvider";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";
import { DataTableAnalysisPoller } from "./DataTableAnalysisPoller";
import { useDataTableRouting } from "./useDataTableRouting";
import { Page404 } from "pages/404/Page404";

const DataTableContext = createContext<DataTableStore | undefined>(undefined);

interface DataTableProviderProps {
  children: ReactNode;
  projectKey: Project["key"];
  tableKey: string | undefined;
  LoadingComponent?: React.ComponentType;
}
class TableNotFoundError extends Error {}
/** Context provider for managing data for data tables and analysis tables */
export const DataTableProvider = ({
  children,
  projectKey,
  tableKey,
  LoadingComponent = AppLoading,
}: DataTableProviderProps) => {
  const tenantId = useTenantContext((s) => s.currentTenant.id);
  const tenantKey = useTenantContext((s) => s.currentTenant.key);
  const [initialTableKey] = useState(tableKey);
  const enqueueFile = useFileUploadContext((s) => s.enqueueFile);
  const cancelFileUploads = useFileUploadContext((s) => s.cancelFileUploads);
  const navigateToTable = useDataTableRouting({ tenantKey, projectKey });
  const [store, setStore] = useState<DataTableStore>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error>();

  // Update the selected table ID in the store when the table key changes
  useEffect(() => {
    if (store !== undefined) {
      const selectedTableId = store.getState().selectedTableId;
      const table = store.getState().tables.find(({ key }) => key === tableKey);
      if (tableKey !== undefined && table === undefined) {
        setError(new TableNotFoundError());
      }
      if (selectedTableId !== table?.id) {
        store.getState().setSelectedTableId(table?.id, false);
      }
    }
  }, [store, tableKey]);

  // Create a new store when the project key changes
  useEffect(() => {
    // Reset state
    setStore(undefined);
    setIsLoading(true);
    setError(undefined);

    createDataTableStore({
      tenantId,
      projectKey,
      initialTableKey,
      navigateToTable,
      enqueueFile,
      cancelFileUploads,
    })
      .then(setStore)
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, [
    cancelFileUploads,
    enqueueFile,
    initialTableKey,
    navigateToTable,
    projectKey,
    tenantId,
  ]);

  if (error !== undefined) {
    if (error instanceof TableNotFoundError) return <Page404 />;
    throw error;
  }

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <DataTableContext.Provider value={store}>
      {/* Renders nothing but must be wrapped by provider */}
      <DataTableAnalysisPoller />
      {children}
    </DataTableContext.Provider>
  );
};

/** Hook for consuming the {@link DataTableContext} */
export const useDataTableContext = <T,>(
  selector: (
    state: Omit<DataTableStoreState, "setSelectedTableId"> & {
      // Setting selected table from outside of provider should always navigate
      setSelectedTableId: (tableId: string | undefined) => void;
    },
  ) => T,
  equalityFn?: (a: T, b: T) => boolean,
) => {
  const store = useContext(DataTableContext);
  assert(isDefined(store), new ContextOutOfBoundsError("DataTableContext"));
  return useStore(store, selector, equalityFn);
};
